<template>
  <div ref="animationBox" />
</template>

<script lang="ts">
export const pixelColors = ['green', 'pink', 'blue', 'violet'] as const
</script>

<script setup lang="ts">
import { captureException } from '@sentry/vue'
import axios from 'axios'
import lottie, { type AnimationItem } from 'lottie-web'

import { useAnimationDataPromisesMap } from '@/state/pixelAnimationData'

export type PixelColor = (typeof pixelColors)[number]

const animationDataPromisesMap = useAnimationDataPromisesMap()

const props = withDefaults(
  defineProps<{
    id: string
    color: PixelColor
    delay?: number
  }>(),
  { delay: 0 }
)
const emit = defineEmits<{ (e: 'complete'): void }>()

const animationBox = ref<HTMLElement>()
const animation = ref<AnimationItem>()
let disposed = false
let playTimeout: ReturnType<typeof setTimeout> | undefined

const initAnimation = async () => {
  if (!animationBox.value) return
  if (!animationDataPromisesMap.value[props.color]) {
    const request = axios
      .get(`/animations/${props.color}.json`)
      .then(({ data }) => data)
      .catch(error => {
        // A failed request must not poison the cache for later instances.
        if (animationDataPromisesMap.value[props.color] === request) {
          delete animationDataPromisesMap.value[props.color]
        }

        const aborted =
          axios.isCancel(error) ||
          (axios.isAxiosError(error) &&
            error.code === 'ECONNABORTED' &&
            error.message === 'Request aborted')
        if (!aborted) {
          // Report once per shared request, rather than once per pixel.
          captureException(error, {
            tags: { component: 'PixelAnimation' },
            extra: { animationColor: props.color }
          })
        }
        return null
      })
    animationDataPromisesMap.value[props.color] = request
  }

  const animationData = await animationDataPromisesMap.value[props.color]
  if (disposed || !animationBox.value || !animationData) return

  animation.value = lottie.loadAnimation({
    name: props.id,
    container: animationBox.value,
    renderer: 'canvas',
    loop: false,
    autoplay: false,
    animationData
  })

  animationBox.value?.childNodes?.forEach(node => {
    if (node instanceof HTMLElement && node.tagName === 'CANVAS') {
      node.setAttribute('aria-hidden', 'true')
      node.setAttribute('role', 'img')
    }
  })

  playTimeout = setTimeout(() => animation.value?.play(), props.delay)

  animation.value?.addEventListener('complete', () => emit('complete'))
}
const destroyAnimation = () => {
  disposed = true
  clearTimeout(playTimeout)
  animation.value?.destroy(props.id)
}

onMounted(initAnimation)
onBeforeUnmount(destroyAnimation)
</script>
